import { useEffect, useState } from "react";
import { DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { AdsStatusCard } from "@/components/AdsStatusCard";
import { QuizStatsCard } from "@/components/QuizStatsCard";
import { AdRailSettingsCard } from "@/components/AdRailSettingsCard";

export default function AdminReferrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { if (user?.role === "admin") api.get("/admin/referral-sources").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, [user]);
  if (user?.role !== "admin") return <p className="text-sm text-muted-foreground" data-testid="admin-forbidden">This page is for admins only.</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Loading...</p>;
  const download = async () => {
    try {
      const r = await api.get("/admin/referral-sources.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `referral-sources-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const max = Math.max(1, ...data.totals.map((t) => t.count));
  return (
    <div>
      <PageHeader title="How users found us" subtitle={`Answers from the onboarding question. ${data.total} response${data.total === 1 ? "" : "s"} so far.`}
        actions={<Button variant="outline" className="rounded-full" onClick={download} data-testid="admin-referrals-csv-button"><DownloadSimple size={16} className="mr-1.5" />Download CSV</Button>} />
      <AdsStatusCard />
      <AdRailSettingsCard />
      <QuizStatsCard />
      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6">
        <Panel title="Totals by source" testid="admin-referrals-totals">
          <ul className="space-y-3">
            {data.totals.map((t) => (
              <li key={t.source} data-testid={`admin-referral-total-${t.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex justify-between text-sm"><span>{t.source}</span><span className="font-mono-num">{t.count}</span></div>
                <div className="h-1.5 rounded-full bg-muted mt-1"><div className="h-1.5 rounded-full bg-cyan-600 transition-all duration-500" style={{ width: `${(t.count / max) * 100}%` }} /></div>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="All answers" testid="admin-referrals-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-muted-foreground border-b border-border">{["Date", "Name", "Email", "Source", "Other"].map((h) => <th key={h} className="text-left py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={`${r.email}-${i}`} className="border-b border-border/60" data-testid={`admin-referral-row-${i}`}>
                    <td className="py-2 font-mono-num whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td><td>{r.name}</td><td>{r.email}</td><td>{r.source}</td><td className="text-muted-foreground">{r.other || "-"}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No answers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
      <p className="text-[11px] text-muted-foreground mt-6" data-testid="admin-contact-email">Contact: <a href="mailto:support@atkinsmedia.io" className="underline">support@atkinsmedia.io</a></p>
    </div>
  );
}
